export type MetadataKey = `metadata.${string}`;

export type MetadataRow = Partial<Record<MetadataKey, unknown>>;

export type Row<T> = T & MetadataRow;

export type GetMetadataValue = (
  metadataKey: MetadataKey,
  entityId: string,
) => unknown;

export type EntityIdGetter<T> = (record: T) => string;

/**
 *
 * @param records - list of entity records
 * @param metadataKeys - metadata keys to include
 * @param getMetadataValue - how to get the metadata value
 * @param getEntityId - how to get the entity's id from the record
 * @returns Entity Id -> Metadata values
 */
export function computeMetadataRows<T>(
  records: readonly T[],
  metadataKeys: readonly MetadataKey[],
  getMetadataValue: GetMetadataValue,
  getEntityId: EntityIdGetter<T>,
): Map<string, MetadataRow> {
  const rows = new Map<string, MetadataRow>();
  if (metadataKeys.length === 0) {
    return rows;
  }
  for (const record of records) {
    const entityId = getEntityId(record);
    const row: MetadataRow = {};
    for (const key of metadataKeys) {
      const value = getMetadataValue(key, entityId);
      if (value !== undefined) {
        row[key] = value;
      }
    }
    rows.set(entityId, row);
  }
  return rows;
}

/**
 *
 * @param records
 * @param metadataByEntity
 * @param getEntityId
 * @returns The projected rows containing the entity records merged with metadata
 */
export function projectRows<T>(
  records: readonly T[],
  metadataByEntity: ReadonlyMap<string, MetadataRow>,
  getEntityId: EntityIdGetter<T>,
): Row<T>[] {
  if (metadataByEntity.size === 0) {
    return records as Row<T>[];
  }
  return records.map((record) => {
    const metadata = metadataByEntity.get(getEntityId(record));
    return metadata ? { ...record, ...metadata } : (record as Row<T>);
  });
}
